/*
 * WR-024 B3: the Horizon grid; one scroll container, sticky table and header;
 * presentational — the page recomputes and saves.
 */
import React, { useState } from 'react';
import {
  weekColumns,
  monthSpans,
  currentWeek,
  todayOffset,
  barBox,
  milestoneCenter,
  linkPath,
  formatWindow,
} from './horizonTime.js';
import {
  buildRows,
  moveRow,
  rowState,
  statusChip,
  predecessorsOf,
  footerLine,
  afterLabel,
} from './horizonPlan.js';
import './HorizonTimeline.css';

function PinIcon() {
  return (
    <svg
      className="hz-pin"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label="Pinned start"
    >
      <path d="M12 17v5" />
      <path d="M9 3h6l-1 7 3 3H7l3-3-1-7z" />
    </svg>
  );
}

function Chevron({ collapsed }) {
  return (
    <svg
      className={`hz-chevron ${collapsed ? 'is-collapsed' : ''}`}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function HorizonTimeline({
  horizon,
  zoom,
  now,
  showLinks,
  selectedId,
  onRowClick,
  onReorder,
  onRemove,
}) {
  const W = zoom === 'month' ? 10 : 40;
  const canvasWidth = 600 + horizon.weeks * W;

  const [collapsed, setCollapsed] = useState(new Set());
  const [hoverId, setHoverId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const activeId = hoverId || selectedId;
  const deps = new Set(activeId ? predecessorsOf(horizon, activeId) : []);
  const todayWeek = currentWeek(horizon.anchorDate, now);
  const lanesById = new Map((horizon.lanes || []).map((l) => [String(l._id || l.id), l]));

  const rows = buildRows(horizon, collapsed);
  const weeks = weekColumns(horizon.anchorDate, horizon.weeks);
  const months = monthSpans(horizon.anchorDate, horizon.weeks);

   // Calculate row positions for links (34px section headers, 36px rows)
   let totalHeight = 0;
   const rowPositions = new Map();
   for (const item of rows) {
    if (item.kind === 'section') {
      rowPositions.set(item.key, { top: totalHeight, y: totalHeight + 17 }); // section header center
      totalHeight += 34;
    } else {
      const itemTop = totalHeight;
      rowPositions.set(item.id, { top: itemTop, y: itemTop + 18, isEntry: item.kind === 'entry' });
      totalHeight += 36;
    }
  }

  const toggleSection = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleRowClick = (kind, id, row) => {
    if (onRowClick) {
      onRowClick({ kind, id, row });
    }
  };

  const handleRowEnter = (id) => {
    setHoverId(id);
  };

  const handleRowLeave = () => {
    setHoverId(null);
  };

  // Drag handlers
  const onDragStart = (e, id) => {
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', id);
      e.dataTransfer.effectAllowed = 'move';
    }
    setDragId(id);
  };

  const onDragEnter = (e, id) => {
    e.preventDefault();
    setDropId(id);
  };

  const onDragOver = (e, id) => {
    e.preventDefault();
    setDropId(id);
  };

  const onDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = e.dataTransfer?.getData('text/plain') || dragId;
    if (sourceId && sourceId !== targetId) {
      // The full processing order — collapsed sections still count.
      const fullOrder = moveRow(horizon, '', 0);
      const targetIndex = fullOrder.indexOf(targetId);
      if (targetIndex !== -1) {
        onReorder(moveRow(horizon, sourceId, targetIndex));
      }
    }
    setDragId(null);
    setDropId(null);
  };

  const onDragEnd = () => {
    setDragId(null);
    setDropId(null);
  };

  // Check if all entries and milestones are empty
  const isEmpty = (!horizon.entries || horizon.entries.length === 0) && (!horizon.milestones || horizon.milestones.length === 0);

  return (
    <div className="hz-timeline">
      <div className="hz-scroll">
        <div className="hz-canvas" style={{ width: canvasWidth }}>
          <div className="hz-head">
            {/* Month row */}
            <div className="hz-head-months">
              <div className="hz-corner" />
              {months.map((m) => {
                const width = m.span * W;
                return (
                  <div
                    key={m.first}
                    className="hz-month"
                    style={{ width: width }}
                  >
                    {m.label}
                  </div>
                );
              })}
            </div>

            {/* Week row */}
            <div className="hz-head-weeks">
              <div className="hz-corner">
                <div className="hz-col hz-c-n" aria-hidden="true">#</div>
                <div className="hz-col hz-c-bst" aria-hidden="true">BST</div>
                <div className="hz-col hz-c-task" aria-hidden="true">Task</div>
                <div className="hz-col hz-c-lane" aria-hidden="true">Lane</div>
                <div className="hz-col hz-c-after" aria-hidden="true">After</div>
                <div className="hz-col hz-c-dur" aria-hidden="true">Dur</div>
                <div className="hz-col hz-c-start" aria-hidden="true">Start</div>
                <div className="hz-col hz-c-end" aria-hidden="true">End</div>
              </div>
              {weeks.map((w) => (
                <div key={w.week} className="hz-week" style={{ width: W }}>
                  {zoom === 'week' && (
                    <>
                      <div className="hz-week-label">{w.label}</div>
                      <div className="hz-week-day">{w.day}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isEmpty ? (
            <div className="hz-empty">No tasks yet — Add tasks or set a milestone.</div>
          ) : (
            <div className="hz-body">
              {rows.map((item, idx) => {
                if (item.kind === 'section') {
                  return (
                    <div
                      key={'section-' + idx}
                      className="hz-section"
                      data-section={item.key}
                      aria-expanded={!item.collapsed}
                      role="button"
                      onClick={() => toggleSection(item.key)}
                    >
                      <div className="hz-section-cells">
                        <Chevron collapsed={item.collapsed} />
                        <span className="hz-section-label">{item.label}</span>
                        <span className="hz-section-meta">{item.meta}</span>
                      </div>
                    </div>
                  );
                }

                // Handle entry or milestone row
                const isEntry = item.kind === 'entry';
                let state = null;
                if (isEntry) {
                  state = rowState(item.row, todayWeek);
                }

                // Get lanes for this row
                let rowLanes = [];
                if (isEntry && item.row.lanes && item.row.lanes.length > 0) {
                  rowLanes = item.row.lanes
                    .map((laneId) => lanesById.get(String(laneId)))
                    .filter(Boolean);
                }

                // Bar box calculation
                const barStyle = isEntry && item.row.start != null ? barBox(item.row.start, item.row.end, W) : null;

                // Row class names
                const rowClasses = [
                  'hz-row',
                  isEntry ? 'hz-row-entry' : 'hz-row-milestone',
                  isEntry ? `is-${state}` : null,
                  activeId === item.id && 'is-active',
                  deps.has(item.id) && 'is-dep',
                  dropId === item.id && 'is-drop-target',
                ]
                  .filter(Boolean)
                  .join(' ');

                const rowProps = {};
                if (isEntry) {
                  rowProps['data-state'] = state;
                 }

                 return (
                  <div
                    key={item.id}
                    className={rowClasses}
                    data-row-id={item.id}
                    data-start={item.row.start}
                    data-end={item.row.end}
                    draggable={true}
                    {...rowProps}
                    onDragStart={(e) => onDragStart(e, item.id)}
                    onDragEnter={(e) => onDragEnter(e, item.id)}
                    onDragOver={(e) => onDragOver(e, item.id)}
                    onDrop={(e) => onDrop(e, item.id)}
                    onDragEnd={onDragEnd}
                    onMouseEnter={() => handleRowEnter(item.id)}
                    onMouseLeave={handleRowLeave}
                    onClick={() => handleRowClick(isEntry ? 'entry' : 'milestone', item.id, item.row)}
                  >
                    <div className="hz-cells">
                      <div className="hz-c-n">{item.n}</div>
                      <div className="hz-c-bst">
                        {isEntry && (item.row.snapshot.bstId || '—').replace(/^BST-/, '')}
                      </div>
                      <div className="hz-c-task">
                        <span className="hz-title">
                          {isEntry ? (item.row.snapshot.title || 'Removed task') : item.row.name}
                        </span>
                        {isEntry && statusChip(state) && (
                          <span className={`hz-chip hz-chip-${statusChip(state).tone}`}>
                            {statusChip(state).label}
                          </span>
                        )}
                        {isEntry && item.row.taskState && onRemove && (
                          <button
                            type="button"
                            className="hz-remove"
                            aria-label="Remove task from Horizon"
                            title="Remove from this Horizon"
                            onClick={e => { e.stopPropagation(); onRemove(item.id); }}
                          >×</button>
                        )}
                      </div>
                      <div className="hz-c-lane">
                        {rowLanes.length > 0 ? (
                          <>
                            <span className={`hz-swatch hz-lane-${rowLanes[0].color}`} />
                            {rowLanes.map((l) => l.name).join(' + ')}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                      <div className="hz-c-after">{afterLabel(horizon, item.id)}</div>
                      <div className="hz-c-dur">{isEntry ? item.row.duration + 'w' : '◆'}</div>
                      <div className="hz-c-start">
                        {isEntry
                          ? item.row.pinnedStart != null && <PinIcon />
                          : item.row.week != null && <PinIcon />}
                        {isEntry && item.row.notBefore != null && (
                          <span className="hz-nb">≥</span>
                        )}
                        <span>{isEntry ? item.row.start : item.row.start}</span>
                      </div>
                      <div className="hz-c-end">{isEntry ? item.row.end : item.row.end}</div>
                    </div>
                    <div className="hz-track" style={{ width: horizon.weeks * W }}>
                      {isEntry && item.row.start != null && (
                        <>
                          <div
                            className={`hz-bar is-${state} hz-lane-${rowLanes[0] ? rowLanes[0].color : 'none'}`}
                            style={barStyle}
                            title={isEntry ? (item.row.snapshot.title || 'Removed task') + ' · ' + formatWindow(horizon.anchorDate, item.row.start, item.row.end) : ''}
                          >
                            {zoom === 'week' && item.row.duration >= 3 && (
                              <span className="hz-bar-label">
                                {isEntry ? item.row.snapshot.title || 'Removed task' : item.row.name}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                      {!isEntry && (
                        <>
                          <div
                            className="hz-diamond"
                            style={{ left: milestoneCenter(item.row.start, W) - 7 }}
                          />
                          <span
                            className="hz-milestone-label"
                            style={{ left: milestoneCenter(item.row.start, W) + 12 }}
                          >
                            {item.row.name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Overlay: today marker and dependency links */}
              <div className="hz-overlay" style={{ width: horizon.weeks * W }}>
                {/* Links */}
                {showLinks && (
                  <svg className="hz-links" width={horizon.weeks * W} height={totalHeight}>
                    {(() => {
                      const renderedRows = rows.filter((r) => r.kind !== 'section');
                      
                      const links = [];
                      for (let i = 0; i < renderedRows.length; i++) {
                        const row = renderedRows[i];
                        if (!row.row.after || row.row.after.length === 0) continue;
                        
                        // Get the rowPos for this row (the target)
                        const rowPos = rowPositions.get(row.id);
                        if (!rowPos) continue;
                        
                          const toX = row.kind === 'entry'
                            ? (row.row.start - 1) * W + 1 // left edge of entry bar
                            : milestoneCenter(row.row.start, W) - 7; // left tip of milestone diamond
                        
                        // Find all rendered predecessor rows
                        const predecessors = [];
                        for (const ref of row.row.after) {
                          for (let j = 0; j < i; j++) {
                            const prevRow = renderedRows[j];
                            if (prevRow.id === String(ref)) {
                              predecessors.push(prevRow);
                              break;
                            }
                          }
                        }
                        
                        for (const prevRow of predecessors) {
                          const prevPos = rowPositions.get(prevRow.id);
                          if (!prevPos) continue;
                          
                          // Calculate fromX based on predecessor type
                          const fromX = prevRow.kind === 'entry'
                            ? prevRow.row.end * W - 2 // right edge of entry bar
                            : milestoneCenter(prevRow.row.start, W) + 7; // right tip of milestone diamond
                          
                          const { d, arrow } = linkPath(fromX, prevPos.y, toX, rowPos.y);
                          const isActive = activeId === row.id;
                          
                          links.push(
                            <React.Fragment key={`link-${prevRow.id}-${row.id}`}>
                              <path
                                className={`hz-link${isActive ? ' is-active' : ''}`}
                                data-from={prevRow.id}
                                data-to={row.id}
                                d={d}
                              />
                              <polygon
                                className={`hz-link-arrow${isActive ? ' is-active' : ''}`}
                                data-from={prevRow.id}
                                data-to={row.id}
                                points={arrow}
                              />
                            </React.Fragment>
                          );
                        }
                      }
                      return links;
                    })()}
                  </svg>
                )}
                
                {(() => {
                  const off = todayOffset(horizon.anchorDate, now);
                  if (off >= 0 && off <= horizon.weeks) {
                    return (
                      <>
                        <div className="hz-today" style={{ left: off * W }} />
                        <div
                          className="hz-today-tag"
                          style={{ left: off * W + 6 }}
                        >
                          Today
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="hz-footer">
        <div className="hz-legend">
          <span className="hz-legend-label">Lanes</span>
          {horizon.lanes &&
            horizon.lanes.map((lane) => (
              <span key={lane._id || lane.id} className="hz-legend-item">
                <span className={`hz-swatch hz-lane-${lane.color}`} />
                {lane.name} · {lane.capacity}
              </span>
            ))}
        </div>
        <div className="hz-explain">
          {(() => {
            const line = activeId ? footerLine(horizon, activeId) : '';
            if (hoverId === null && line) {
              return 'Selected: ' + line;
            }
            return hoverId !== null ? line : '';
          })()}
        </div>
      </div>
    </div>
  );
}

export default HorizonTimeline;
